import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, EmptyState, InlineError, Input, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useCgAccess } from '../cgOrders/hooks';
import {
  ALL_NOTES,
  folderNameProblem,
  inFolder,
  notePreview,
  noteTitle,
  searchNotes,
  timeSections,
} from './notesLens';
import { EXPORT_ACTIONS, shortcutFor } from './noteEditorConfig';
import {
  cleanHtml,
  countWords,
  parseContent,
  stringifyContent,
  titleFromDoc,
  toSlackMarkdown,
} from './noteSerialize';
import {
  useCreateFolder,
  useMoveFolder,
  useCreateNote,
  useDeleteFolder,
  useDeleteNotes,
  useMoveNotes,
  useNoteFolders,
  useNotes,
  useUpdateNote,
} from './queries';
import {
  ancestorsOf,
  buildTree,
  canDropFolder,
  descendantIds,
  folderPath,
  loadExpanded,
  saveExpanded,
} from './folderTree';
import type { FolderNode as FolderNodeData } from './folderTree';
import {
  allSelected,
  dragPayload,
  dragSet,
  FOLDER_DRAG_MIME,
  NOTE_DRAG_MIME,
  parseDragPayload,
  selectAll,
  selectionLabel,
  selectionWithin,
  toggleSelected,
} from './noteBulk';
import type { DocNode } from './noteSerialize';
import type { Note } from './notesLens';
import type { Editor } from '@tiptap/react';

/**
 * My Notes: folders, list, editor — full bleed.
 *
 * ── Why the editor is lazy ─────────────────────────────────────────────────
 *
 * Tiptap and ProseMirror are roughly 110KB gzipped. Loading them in the main
 * bundle would slow the first paint of Home, Projects and every other screen
 * for a feature most sessions never open. The import below splits them into
 * their own chunk that arrives when somebody actually opens a note.
 *
 * ── Why the content column holds JSON ──────────────────────────────────────
 *
 * A mention carries four attributes and a task item carries its checked state.
 * Both survive JSON exactly; through HTML they survive only as well as the
 * parser and the sanitizer agree. parseContent still reads the plain text that
 * notes written before the editor hold, so nothing needed migrating.
 */

const NoteEditor = lazy(() =>
  import('./NoteEditor').then((module) => ({ default: module.NoteEditor })),
);

/** Long enough not to write on every keystroke, short enough to feel saved. */
const SAVE_DELAY_MS = 400;

/**
 * Nesting depth as a CSS variable rather than a computed padding.
 *
 * The row could carry `paddingLeft: calc(var(--space-3) * 2)` directly, but an
 * inline calc() over a custom property is a value the stylesheet cannot
 * override and that no theme can adjust — and the indent step belongs with the
 * rest of the pane's spacing, in the stylesheet. The component publishes the
 * depth; components.css decides what a level is worth.
 */
function depthStyle(depth: number): React.CSSProperties {
  return { '--folder-depth': depth } as React.CSSProperties;
}

interface FolderBranchView {
  activeId: string;
  dropTarget: string | null;
  expanded: ReadonlySet<string>;
  creatingUnder: string | null | undefined;
  counts: Map<string, number>;
}

interface FolderBranchActions {
  select: (id: string) => void;
  toggle: (id: string) => void;
  addSub: (id: string) => void;
  remove: (node: FolderNodeData) => void;
  dragStart: (id: string, event: React.DragEvent) => void;
  dragOver: (id: string, event: React.DragEvent) => void;
  dragLeave: () => void;
  drop: (id: string, event: React.DragEvent) => void;
}

export interface FolderBranchProps {
  nodes: FolderNodeData[];
  view: FolderBranchView;
  actions: FolderBranchActions;
  /** The inline "new sub-folder" field, rendered under whichever row asked. */
  createInput: React.ReactNode;
}

/**
 * One level of the folder tree, and every level below it.
 *
 * Recursive rather than a loop over a pre-flattened list, because the DOM has
 * to nest too: a collapsed branch must take its whole subtree off the screen,
 * and a flat render would need every row to know whether some ancestor was
 * shut.
 *
 * Declared at module scope on purpose. A component defined inside NotesPage
 * gets a new identity on every render, which unmounts and remounts this whole
 * subtree — and the inline rename/create field inside it would lose focus on
 * the first keystroke.
 *
 * Indentation is padding on the row rather than a nested container with a
 * margin: the row is also the drop target, and a target that starts 40px in
 * has a dead strip down the left of the pane where a drag looks like it should
 * land and does not.
 */
export function FolderBranch({ nodes, view, actions, createInput }: FolderBranchProps) {
  return (
    <>
      {nodes.map((node) => {
        const open = view.expanded.has(node.id);
        const hasChildren = node.children.length > 0;
        return (
          <div key={node.id} className="notes-folder-branch">
            <div
              className={`notes-folder-row${
                view.dropTarget === node.id ? ' notes-folder-row--drop' : ''
              }`}
              style={depthStyle(node.depth)}
              draggable
              onDragStart={(event) => actions.dragStart(node.id, event)}
              onDragOver={(event) => actions.dragOver(node.id, event)}
              onDragLeave={actions.dragLeave}
              onDrop={(event) => actions.drop(node.id, event)}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="notes-folder-twist"
                  aria-expanded={open}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
                  onClick={() => actions.toggle(node.id)}
                >
                  {open ? '▾' : '▸'}
                </button>
              ) : (
                // A spacer, not nothing: without it a childless folder's name
                // sits one chevron-width left of its siblings and the level
                // stops reading as a level.
                <span className="notes-folder-twist notes-folder-twist--leaf" aria-hidden="true" />
              )}
              <button
                type="button"
                className={`notes-folder${view.activeId === node.id ? ' notes-folder--active' : ''}${
                  view.dropTarget === node.id ? ' notes-folder--drop' : ''
                }`}
                aria-current={view.activeId === node.id ? 'true' : undefined}
                onClick={() => actions.select(node.id)}
              >
                <span className="notes-folder-name">{node.name}</span>
                <span className="notes-folder-count">{view.counts.get(node.id) ?? 0}</span>
              </button>
              <button
                type="button"
                className="notes-folder-action"
                aria-label={`New folder inside ${node.name}`}
                title={`New folder inside ${node.name}`}
                onClick={() => actions.addSub(node.id)}
              >
                +
              </button>
              <button
                type="button"
                className="notes-folder-action"
                aria-label={`Delete folder ${node.name}`}
                onClick={() => actions.remove(node)}
              >
                ×
              </button>
            </div>

            {view.creatingUnder === node.id ? (
              <div className="notes-folder-new-nested" style={depthStyle(node.depth + 1)}>
                {createInput}
              </div>
            ) : null}

            {hasChildren && open ? (
              <FolderBranch
                nodes={node.children}
                view={view}
                actions={actions}
                createInput={createInput}
              />
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function NotesPage() {
  const [searchParams] = useSearchParams();
  const { client } = useAuth();
  const cgAccess = useCgAccess();
  const notes = useNotes();
  const folders = useNoteFolders();
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const createFolder = useCreateFolder();
  const deleteFolder = useDeleteFolder();
  const deleteNotes = useDeleteNotes();
  const moveNotes = useMoveNotes();
  const moveFolder = useMoveFolder();

  const [folderId, setFolderId] = useState<string>(ALL_NOTES);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [focusMode, setFocusMode] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [folderError, setFolderError] = useState('');
  const [copied, setCopied] = useState('');
  const [words, setWords] = useState({ words: 0, characters: 0 });
  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(() => new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [folderPillOpen, setFolderPillOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  // Which branches are open. Seeded from localStorage so the tree does not
  // collapse every time the page reloads.
  const [expanded, setExpanded] = useState<Set<string>>(() => loadExpanded(window.localStorage));
  // Where a new folder is being typed. Three states, and they are all needed:
  //   undefined — nothing is being created
  //   null      — a top-level folder
  //   <id>      — a sub-folder of that one
  // A boolean plus a parent would let "not creating" and "creating at the
  // root" look identical, which is how two inputs end up on screen at once.
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined);

  const searchRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const draftRef = useRef<DocNode | null>(null);
  const editingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linkedNoteOpened = useRef(false);

  const all = notes.data ?? [];
  const folderList = folders.data ?? [];
  const tree = buildTree(folderList);
  const visible = searchNotes(inFolder(all, folderId), search);
  const sections = timeSections(visible, new Date());
  const selected = all.find((note) => note.id === selectedId) ?? null;

  /** Write the editor's current document back to the note it belongs to. */
  const flush = useCallback(() => {
    const id = editingRef.current;
    const doc = draftRef.current;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (id === null || doc === null) return;
    // Title and preview are DERIVED, never typed. The first line is the title
    // — that is the whole reason there is no title field.
    updateNote.mutate({ id, title: titleFromDoc(doc, ''), content: stringifyContent(doc) });
  }, [updateNote]);

  const open = useCallback(
    (note: Note) => {
      if (editingRef.current !== null && editingRef.current !== note.id) {
        flush();
      }
      editingRef.current = note.id;
      draftRef.current = parseContent(note.content);
      setSelectedId(note.id);
      setTitleDraft(note.title);
      setWords(countWords(parseContent(note.content)));
    },
    [flush],
  );

  useEffect(() => {
    const id = searchParams.get('note');
    if (!linkedNoteOpened.current && notes.data !== undefined) {
      linkedNoteOpened.current = true;
      const note = all.find((row) => row.id === id);
      if (note !== undefined) open(note);
    }
  }, [all, notes.data, open, searchParams]);

  const addNote = useCallback(async () => {
    const created = await createNote.mutateAsync({
      folderId: folderId === ALL_NOTES ? null : folderId,
    });
    open(created);
  }, [createNote, folderId, open]);

  // ── Shortcuts ────────────────────────────────────────────────────────────
  //
  // Bound on the window, not on a container: the editor takes focus for most
  // of this screen's life, and a handler on a div never sees the keystroke.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const shortcut = shortcutFor(event);
      if (shortcut === null) return;
      // Only after a match: preventDefault on everything would take Cmd+C.
      event.preventDefault();
      if (shortcut === 'newNote') void addNote();
      if (shortcut === 'search') searchRef.current?.focus();
      if (shortcut === 'focus') setFocusMode((value) => !value);
      if (shortcut === 'checklist') editorRef.current?.chain().focus().toggleTaskList().run();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [addNote]);

  useEffect(() => {
    saveExpanded(window.localStorage, expanded);
  }, [expanded]);

  // A pending save must not die with the page.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /** What a bulk action applies to: ticked AND still on screen. */
  const actionable = selectionWithin(selection, visible);

  function toggleExpanded(id: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** Open every branch above a folder, so a selection is never hidden. */
  function revealFolder(id: string) {
    setExpanded((current) => new Set([...current, ...ancestorsOf(folderList, id)]));
  }

  function dropFolderOn(draggedId: string, target: string | null) {
    if (!canDropFolder(folderList, draggedId, target)) {
      return;
    }
    moveFolder.mutate({ id: draggedId, parentId: target });
    if (target !== null) {
      setExpanded((current) => new Set([...current, target]));
    }
  }

  function clearSelection() {
    setSelection(new Set());
  }

  function dropOnFolder(event: React.DragEvent, target: string | null) {
    event.preventDefault();
    setDropTarget(null);

    // A folder drag and a note drag arrive at the same handler. They carry
    // different MIME types precisely so this can tell them apart — without
    // that, dropping a folder would try to re-file it as if it were a note.
    const draggedFolder = event.dataTransfer.getData(FOLDER_DRAG_MIME);
    if (draggedFolder !== '') {
      dropFolderOn(draggedFolder, target);
      return;
    }

    const ids = parseDragPayload(event.dataTransfer.getData(NOTE_DRAG_MIME));
    if (ids.length === 0) return;
    moveNotes.mutate({ ids, folderId: target });
    clearSelection();
  }

  function deleteMany(ids: string[]) {
    if (ids.length === 0) return;
    // The open note may be among them; its queued save would land against a
    // row that no longer exists.
    if (selectedId !== null && ids.includes(selectedId)) {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      editingRef.current = null;
      draftRef.current = null;
      setSelectedId(null);
    }
    deleteNotes.mutate(ids);
    clearSelection();
  }

  function commitTitle() {
    const id = editingRef.current;
    if (id === null) return;
    // Sent as typed, blank included: an empty title is what switches the row
    // back to deriving from the first line, so it has to reach the column.
    updateNote.mutate({ id, title: titleDraft.trim() });
  }

  function onDocChange(doc: DocNode) {
    draftRef.current = doc;
    setWords(countWords(doc));
    const id = editingRef.current;
    if (id === null) return;
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      updateNote.mutate({
        id,
        title: titleFromDoc(doc, ''),
        content: stringifyContent(doc),
      });
    }, SAVE_DELAY_MS);
  }

  function runExport(action: (typeof EXPORT_ACTIONS)[number]['id']) {
    setExportOpen(false);
    const editor = editorRef.current;
    if (editor === null) return;
    if (action === 'pdf') {
      // The attribute is what the @media print block is scoped to, so the
      // rest of the app keeps printing normally. Removed straight after: the
      // print dialog is modal, so by the time this line runs the browser has
      // finished with the page.
      document.documentElement.setAttribute('data-printing', 'note');
      window.print();
      document.documentElement.removeAttribute('data-printing');
      return;
    }
    const text = action === 'slack' ? toSlackText(editor) : cleanHtml(editor.getHTML());
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(action);
        setTimeout(() => setCopied(''), 2000);
      },
      () => setCopied(''),
    );
  }

  function startFolder(parentId: string | null) {
    setCreatingUnder(parentId);
    setNewFolder('');
    setFolderError('');
    // Open the branch first: an input that appears inside a collapsed folder
    // is an input nobody can see.
    if (parentId !== null) {
      setExpanded((current) => new Set([...current, parentId]));
    }
  }

  function cancelFolder() {
    setCreatingUnder(undefined);
    setNewFolder('');
    setFolderError('');
  }

  function addFolder() {
    const parentId = creatingUnder ?? null;
    // Checked against SIBLINGS, matching the per-parent unique index.
    const problem = folderNameProblem(newFolder, folderList, parentId);
    if (problem !== null) {
      setFolderError(problem);
      return;
    }
    setFolderError('');
    createFolder.mutate(
      { name: newFolder.trim(), parentId },
      {
        onSuccess: () => {
          setNewFolder('');
          if (parentId !== null) {
            setExpanded((current) => new Set([...current, parentId]));
          }
          setCreatingUnder(undefined);
        },
        onError: (cause: unknown) => {
          setFolderError(
            cause instanceof Error ? cause.message : 'The folder could not be created.',
          );
        },
      },
    );
  }

  /**
   * Delete a folder, and stop standing in it.
   *
   * The database cascades to sub-folders, so the pane the person is looking at
   * may be several levels below the row they clicked. Checking only the row
   * itself would leave the list filtered by a folder id that no longer exists,
   * which shows an empty middle column and no way to explain it.
   */
  function removeFolder(node: FolderNodeData) {
    const gone = new Set([node.id, ...descendantIds(folderList, node.id)]);
    deleteFolder.mutate(node.id);
    if (gone.has(folderId)) {
      setFolderId(ALL_NOTES);
    }
    if (creatingUnder != null && gone.has(creatingUnder)) {
      cancelFolder();
    }
  }

  // Counted once per render rather than filtering the whole note list inside
  // every row — a deep tree would otherwise be quadratic in the number of
  // notes.
  const folderCounts = new Map<string, number>();
  for (const note of all) {
    if (note.folder_id !== null) {
      folderCounts.set(note.folder_id, (folderCounts.get(note.folder_id) ?? 0) + 1);
    }
  }

  // Every folder as a full path, in tree order, for the move menu.
  const folderChoices = folderList
    .map((folder) => ({ id: folder.id, path: folderPath(folderList, folder.id) }))
    .sort((a, b) => a.path.localeCompare(b.path));

  const folderActions = {
    select: (id: string) => {
      setFolderId(id);
      revealFolder(id);
    },
    toggle: toggleExpanded,
    addSub: startFolder,
    remove: removeFolder,
    dragStart: (id: string, event: React.DragEvent) => {
      // A DIFFERENT MIME type from a note drag, which is what lets one drop
      // handler tell a re-parent from a re-file. Same type for both would make
      // dropping a folder try to move it as though it were a note.
      event.dataTransfer.setData(FOLDER_DRAG_MIME, id);
      event.dataTransfer.effectAllowed = 'move';
      event.stopPropagation();
    },
    dragOver: (id: string, event: React.DragEvent) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDropTarget(id);
    },
    dragLeave: () => setDropTarget(null),
    drop: (id: string, event: React.DragEvent) => {
      event.stopPropagation();
      dropOnFolder(event, id);
    },
  };

  const folderInput = (
    <div className="notes-folder-new">
      <Input
        label="Folder name"
        labelHidden
        placeholder="Folder name"
        className="notes-folder-input"
        value={newFolder}
        autoFocus
        onChange={(event) => setNewFolder(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') addFolder();
          if (event.key === 'Escape') cancelFolder();
        }}
      />
      <Button size="sm" onClick={addFolder}>
        Add
      </Button>
    </div>
  );

  if (notes.isPending || folders.isPending) {
    return <Skeleton lines={8} label="Loading your notes" />;
  }
  if (notes.isError) {
    return <InlineError>Your notes could not be loaded. Refresh to try again.</InlineError>;
  }

  return (
    <div className="notes-app" data-focus={focusMode}>
      {/* ── Header ── */}
      <header className="notes-header">
        {/* Search used to float across the full width here, detached from the
            list it filters. It now lives in that list's own header, where the
            thing it acts on is directly beneath it. */}
        <span className="notes-header-title">My Notes</span>
        <div className="notes-header-actions">
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={focusMode}
            onClick={() => setFocusMode((value) => !value)}
          >
            {focusMode ? 'Exit focus' : 'Focus mode'}
          </Button>
          {selected === null ? null : (
            <>
              <div className="notes-folder-pill">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-haspopup="menu"
                  aria-expanded={folderPillOpen}
                  onClick={() => setFolderPillOpen((value) => !value)}
                >
                  {/* The full path, not the leaf name: two folders called
                      Drafts under different parents are both legal now, and a
                      pill showing only "Drafts" names neither of them. */}
                  {`📁 ${selected.folder_id === null ? 'All Notes' : folderPath(folderList, selected.folder_id) || 'All Notes'} ▾`}
                </Button>
                {folderPillOpen ? (
                  <div className="notes-export-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="notes-export-item"
                      onClick={() => {
                        moveNotes.mutate({ ids: [selected.id], folderId: null });
                        setFolderPillOpen(false);
                      }}
                    >
                      All Notes
                    </button>
                    {folderChoices.map((choice) => (
                      <button
                        key={choice.id}
                        type="button"
                        role="menuitem"
                        className="notes-export-item"
                        onClick={() => {
                          moveNotes.mutate({ ids: [selected.id], folderId: choice.id });
                          setFolderPillOpen(false);
                        }}
                      >
                        {choice.path}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={selected.is_pinned}
                onClick={() =>
                  updateNote.mutate({ id: selected.id, isPinned: !selected.is_pinned })
                }
              >
                {selected.is_pinned ? 'Unpin' : 'Pin'}
              </Button>
              <div className="notes-export">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-haspopup="menu"
                  aria-expanded={exportOpen}
                  onClick={() => setExportOpen((value) => !value)}
                >
                  Export ▾
                </Button>
                {exportOpen ? (
                  <div className="notes-export-menu" role="menu">
                    {EXPORT_ACTIONS.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        role="menuitem"
                        className="notes-export-item"
                        onClick={() => runExport(action.id)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Delete this note"
                onClick={() => {
                  // Through the same path as a bulk delete, so the queued-save
                  // guard exists once rather than in two places that drift.
                  deleteMany([selected.id]);
                }}
              >
                🗑 Delete
              </Button>
            </>
          )}
          {copied === '' ? null : <span className="notes-copied">Copied</span>}
        </div>
      </header>

      <div className="notes-panes">
        {/* ── Folders ── */}
        <nav className="notes-folders" aria-label="Note folders">
          <button
            type="button"
            className={`notes-folder${folderId === ALL_NOTES ? ' notes-folder--active' : ''}${
              dropTarget === ALL_NOTES ? ' notes-folder--drop' : ''
            }`}
            aria-current={folderId === ALL_NOTES ? 'true' : undefined}
            onClick={() => setFolderId(ALL_NOTES)}
            onDragOver={(event) => {
              // preventDefault is what makes an element a drop target at all.
              // Without it the browser refuses the drop and shows the
              // no-entry cursor, which reads as the feature being broken.
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDropTarget(ALL_NOTES);
            }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(event) => dropOnFolder(event, null)}
          >
            All Notes
            <span className="notes-folder-count">{all.length}</span>
          </button>

          <FolderBranch
            nodes={tree}
            view={{
              activeId: folderId,
              dropTarget,
              expanded,
              creatingUnder,
              counts: folderCounts,
            }}
            actions={folderActions}
            createInput={folderInput}
          />

          {creatingUnder === null ? (
            folderInput
          ) : (
            <Button variant="ghost" size="sm" onClick={() => startFolder(null)}>
              + New Folder
            </Button>
          )}
          {folderError === '' ? null : <InlineError>{folderError}</InlineError>}
          <p className="notes-folder-hint">
            Deleting a folder keeps its notes, in All Notes. Sub-folders go with it.
          </p>
        </nav>

        {/* ── List ── */}
        <div className="notes-list-panel">
          <div className="notes-list-head">
            <Input
              ref={searchRef}
              label="Search notes"
              labelHidden
              type="search"
              placeholder="Search notes"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {/* Compact, and an icon rather than a green block: it sits beside
                a text field in a 48px bar, where a full-width button would be
                the loudest thing on the screen. */}
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={selectMode}
              onClick={() => {
                setSelectMode((value) => !value);
                clearSelection();
              }}
            >
              {selectMode ? 'Done' : 'Select'}
            </Button>
            <button
              type="button"
              className="notes-new-note"
              aria-label="New note"
              title="New note (Cmd+N)"
              onClick={() => {
                void addNote();
              }}
            >
              +
            </button>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title={search.trim() === '' ? 'No notes here yet' : 'Nothing matches that search'}
              description={
                search.trim() === ''
                  ? 'Press New Note to start one.'
                  : 'Try a different word, or clear the search.'
              }
            />
          ) : (
            <div className="notes-list">
              {sections.map((section) => (
                <section key={section.key} aria-label={section.label}>
                  <h2 className="notes-list-heading">{section.label}</h2>
                  {section.notes.map((note) => (
                    <div
                      key={note.id}
                      className={`notes-row-wrap${note.id === selectedId ? ' notes-row-wrap--active' : ''}`}
                      draggable
                      onDragStart={(event) => {
                        // Dragging a card inside the selection carries the
                        // whole selection; dragging one outside it carries
                        // only that card. See dragSet.
                        const ids = dragSet(note.id, selection);
                        event.dataTransfer.setData(NOTE_DRAG_MIME, dragPayload(ids));
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        setMenuFor(note.id);
                      }}
                    >
                      {selectMode ? (
                        <input
                          type="checkbox"
                          className="notes-row-check"
                          aria-label={`Select ${noteTitle(note)}`}
                          checked={selection.has(note.id)}
                          onChange={() =>
                            setSelection((current) => toggleSelected(current, note.id))
                          }
                        />
                      ) : null}
                      <button
                        type="button"
                        className="notes-row"
                        aria-current={note.id === selectedId ? 'true' : undefined}
                        onClick={() => open(note)}
                      >
                        <span className="notes-row-title">{noteTitle(note)}</span>
                        <span className="notes-row-preview">{notePreview(note)}</span>
                      </button>
                      {menuFor === note.id ? (
                        <div className="notes-context-menu" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="notes-export-item"
                            onClick={() => {
                              setMenuFor(null);
                              deleteMany([note.id]);
                            }}
                          >
                            Delete Note
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="notes-export-item"
                            onClick={() => setMenuFor(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}

          {selectMode ? (
            <div className="notes-batch-bar" role="group" aria-label="Bulk actions">
              <label className="notes-batch-all">
                <input
                  type="checkbox"
                  aria-label="Select all notes"
                  checked={allSelected(selection, visible)}
                  onChange={(event) =>
                    setSelection(event.target.checked ? selectAll(visible) : new Set())
                  }
                />
                <span>{selectionLabel(actionable.length)}</span>
              </label>
              <Button
                variant="danger"
                size="sm"
                disabled={actionable.length === 0}
                onClick={() => deleteMany(actionable)}
              >
                Delete Selected
              </Button>
              <select
                className="note-toolbar-select"
                aria-label="Move to Folder"
                value=""
                disabled={actionable.length === 0}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === '') return;
                  moveNotes.mutate({
                    ids: actionable,
                    folderId: value === ALL_NOTES ? null : value,
                  });
                  clearSelection();
                }}
              >
                <option value="">Move to Folder…</option>
                <option value={ALL_NOTES}>All Notes</option>
                {folderList.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        {/* ── Editor ── */}
        <div className="notes-editor">
          {selected === null ? (
            <EmptyState
              title="Select a note, or create one"
              description="Your notes are private to you."
            />
          ) : (
            <>
              {/* An explicit title, and optional. Left blank, noteTitle falls
                  back to the first line of the body — so this is a place to
                  name a note, not another empty box to fill in. */}
              <input
                className="notes-title-input"
                aria-label="Note title"
                placeholder={titleFromDoc(parseContent(selected.content), 'Untitled note')}
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.target.value)}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitTitle();
                    editorRef.current?.commands.focus();
                  }
                }}
              />
              <Suspense fallback={<Skeleton lines={10} label="Loading the editor" />}>
                <NoteEditor
                  // Remounting on the note id is what swaps documents. Feeding a
                  // new value into a live editor would fight the selection.
                  key={selected.id}
                  value={parseContent(selected.content)}
                  onChange={onDocChange}
                  onBlur={flush}
                  client={client}
                  sources={{ cgMember: cgAccess.data === true }}
                  onReady={(editor) => {
                    editorRef.current = editor;
                  }}
                />
              </Suspense>
            </>
          )}
        </div>
      </div>

      <footer className="notes-footer">
        <span>
          {words.words} {words.words === 1 ? 'word' : 'words'} · {words.characters}{' '}
          {words.characters === 1 ? 'character' : 'characters'}
        </span>
      </footer>
    </div>
  );
}

/**
 * Slack text for whatever is in the editor.
 *
 * The serializer stays in the main bundle rather than the lazy chunk: it is a
 * few hundred bytes of pure functions and keeping it here means the export
 * menu does not have to wait on a dynamic import to answer a click.
 */
function toSlackText(editor: Editor): string {
  return toSlackMarkdown(editor.getJSON() as DocNode);
}
